import { types as api } from '@mesa-labs/mesa-api';
import * as types from '@mesa-labs/mesa-api/dist/types';
import React, {
  ChangeEvent, useEffect, useMemo, useState,
} from 'react';
import styled from 'styled-components';
import {
  Checkbox,
  DownloadButton,
  Input,
  MesaColumn,
  MesaTable,
  Search,
  useMesaTableServerState,
} from '@mesa-labs/mesa-ui';

import { useLocation } from 'react-router-dom';
import Select, { SingleValue } from 'react-select';

import {
  asExportFilter,
  Selection,
} from '../../utils';
import { DefaultPaginationLimits } from '../common/PaginationLimits';
import { exportAllExternalVendorsV2, useGetAllExternalVendorsV2Query } from '../../redux/api/externalVendors';
import {
  FilterCell, FilterControls, FilterRow, FilterSection, FilterTitle,
} from '../common/Filters';
import { useDispatch, useSelector } from '../../redux/hooks';
import {
  updateIsPreferredVendor,
  updateIsInternalVendor,
  updateIsVendorInHubSpot,
  updateMaximumWeightedEligibleAveragePaymentTenor, updateMinimumPercentageProjectedEligibleInvoiceSpend, updateMinimumEligibleInvoiceSpend,
  updateMinimumWeightedEligibleAveragePaymentTenor,
  updateSearchTerm, updateUseLeadListFilters, updateVendorLocale
} from '../../redux/slices/externalVendors';
import PartnerSelect from '../common/PartnerSelect';
import VendorLocaleSelect from '../common/VendorLocaleSelect';
import useSyncQueryStringParamsToRedux from '../../hooks/useSyncQueryStringParamsToRedux';

const VendorsPageContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const TableWrapper = styled.div`
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const TableCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 18px;
  border-bottom: 1px solid #E5E7EB;
`;

const TableCardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13.5px;
  font-weight: 700;
  color: #111827;
`;

const TableTitleIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 5px;
  background: #E0E7FF;
`;

const TableMeta = styled.span`
  font-size: 11.5px;
  font-weight: 400;
  color: #9CA3AF;
`;

export const BooleanSelectionOptions: readonly Selection[] = [
  {
    label: '(Not Set)',
    value: '',
  },
  {
    label: 'True',
    value: 'true',
  },
  {
    label: 'False',
    value: 'false',
  },
];

const ExternalVendorMesaColumns: MesaColumn<api.ExternalVendorDataResponse>[] = [
  {
    id: 'partnerName',
    label: 'Partner',
    sortField: 'partnerId',
    accessorFn: (row) => api.Partners[row.partnerId],
  },
  {
    key: 'externalVendorId', label: 'External Id', sortField: 'externalVendorId',
  },
  {
    key: 'externalVendorName', label: 'External Name', sortField: 'externalVendorName', flex: true,
  },
  {
    key: 'eligibleInvoiceSpend', label: 'Eligible Spend (T12M)', type: 'currency', sortField: 'eligibleInvoiceSpend', currencyCode: 'USD',
  },
  {
    key: 'eligibleInvoiceSpendT6m', label: 'Eligible Spend (T6M)', type: 'currency', sortField: 'eligibleInvoiceSpendT6m', currencyCode: 'USD',
  },
  {
    key: 'percentageProjectedEligibleInvoiceSpendT6m', label: '% Projected Eligible Spend (T6M)', type: 'number', sortField: 'percentageProjectedEligibleInvoiceSpendT6m',
  },
  {
    key: 'weightedEligibleAveragePaymentTenor', label: 'Eligible WAPT (T12M)', type: 'number', sortField: 'weightedEligibleAveragePaymentTenor',
  },
  {
    key: 'weightedEligibleAverageInvoiceTenor', label: 'Eligible WAIT (T12M)', type: 'number', sortField: 'weightedEligibleAverageInvoiceTenor',
  },
  {
    key: 'weightedEligibleAverageLedgerLatenessT12m', label: 'Eligible WALL (T12M)', type: 'number', sortField: 'weightedEligibleAverageLedgerLatenessT12m',
  },
  {
    key: 'internalVendorState', label: 'Internal Status', type: 'status', sortField: 'internalVendorState',
  },
  {
    key: 'classification', label: 'Classification', sortField: 'classification',
  },
  {
    key: 'signedUpAt', label: 'Sign Up Date', type: 'date', sortField: 'signedUpAt',
  },
  {
    key: 'activatedAt', label: 'Activation Date', type: 'date', sortField: 'activatedAt',
  },
];

type ExternalVendorsPageTemplateProps = {
  businessEntityScope: api.BusinessEntityScope;
};

function ExternalVendorsPageTemplate(props: ExternalVendorsPageTemplateProps): React.ReactElement {
  const { businessEntityScope } = props;
  const qualifier = businessEntityScope === api.BusinessEntityScope.External ? 'external' : 'internal';
  const [partner, setPartner] = useState<api.Partners | undefined>(undefined);
  const dispatch = useDispatch();
  const location = useLocation();
  const vendorLocale = useSelector((state) => state.externalVendors.vendorLocale);
  const searchTerm = useSelector((state) => state.externalVendors.searchTerm);
  const useLeadListFilters = useSelector((state) => state.externalVendors.useLeadListFilters) || false;
  const isInternalVendor = useSelector((state) => state.externalVendors.isInternalVendor);
  const isPreferredVendor = useSelector((state) => state.externalVendors.isPreferredVendor);
  const isVendorInHubSpot = useSelector((state) => state.externalVendors.isVendorInHubSpot);
  const minimumPercentageProjectedEligibleInvoiceSpend = useSelector((state) => state.externalVendors.minimumPercentageProjectedEligibleInvoiceSpend);
  const minimumEligibleInvoiceSpend = useSelector((state) => state.externalVendors.minimumEligibleInvoiceSpend);
  const minimumWeightedEligibleAveragePaymentTenor = useSelector((state) => state.externalVendors.minimumWeightedEligibleAveragePaymentTenor);
  const maximumWeightedEligibleAveragePaymentTenor = useSelector((state) => state.externalVendors.maximumWeightedEligibleAveragePaymentTenor);

  useSyncQueryStringParamsToRedux({ sliceName: 'externalVendors' });

  const server = useMesaTableServerState({ initialPageSize: 10, columns: ExternalVendorMesaColumns, persistKey: 'externalVendorsPageTemplate' });

  // page/limit/sort left the externalVendors slice for useMesaTableServerState, so
  // useSyncQueryStringParamsToRedux can no longer deep-link them — bridge
  // those four URL params into the hook once on mount. Order matters here:
  // the hook's reducer resets pageIndex to 0 whenever sorting or page size
  // changes (by design, elsewhere in the app), so setPageNumber must run last
  // or a `page` param gets silently clobbered back to the first page.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlPage = params.get('page');
    const urlLimit = params.get('limit');
    const urlSortField = params.get('sortField');
    const urlSortDirection = params.get('sortDirection');
    if (urlSortField) server.setSorting([{ id: urlSortField, desc: urlSortDirection === 'desc' }]);
    if (urlLimit) server.setPageSize(Number(urlLimit));
    if (urlPage) server.setPageNumber(Number(urlPage));
  }, []);

  const [searchInput, setSearchInput] = useState<string | undefined>(searchTerm);
  const [minimumPercentageProjectedEligibleInvoiceSpendInput, setMinimumPercentageProjectedEligibleInvoiceSpendInput] = useState<string>(minimumPercentageProjectedEligibleInvoiceSpend?.toString() || '');
  const [minimumEligibleInvoiceSpendInput, setMinimumEligibleInvoiceSpendInput] = useState<string>(minimumEligibleInvoiceSpend?.toString() || '');
  const [minimumWeightedEligibleAveragePaymentTenorInput, setMinimumWeightedEligibleAveragePaymentTenorInput] = useState<string>(minimumWeightedEligibleAveragePaymentTenor?.toString() || '');
  const [maximumWeightedEligibleAveragePaymentTenorInput, setMaximumWeightedEligibleAveragePaymentTenorInput] = useState<string>(maximumWeightedEligibleAveragePaymentTenor?.toString() || '');
  const [isInternalVendorInput, setIsInternalVendorInput] = useState<Selection>(BooleanSelectionOptions.find((o) => o.value === isInternalVendor?.toString() || '') || BooleanSelectionOptions[0]);
  const [isPreferredVendorInput, setIsPreferredVendorInput] = useState<Selection>(BooleanSelectionOptions.find((o) => o.value === isPreferredVendor?.toString() || '') || BooleanSelectionOptions[0]);
  const [isVendorInHubSpotInput, setIsVendorInHubSpotInput] = useState<Selection>(BooleanSelectionOptions.find((o) => o.value === isVendorInHubSpot?.toString() || '') || BooleanSelectionOptions[0]);

  const filters: types.ExternalVendorFilterParams = {
    partnerId: partner,
    ...server.queryArgs,
    sortDirection: server.queryArgs.sortDirection as api.SortDirection | undefined,
    search: searchTerm,
    isInternalVendor,
    isPreferredVendor,
    isVendorInHubSpot,
    minimumPercentageProjectedEligibleInvoiceSpendT6m: minimumPercentageProjectedEligibleInvoiceSpend,
    minimumEligibleInvoiceSpendT6m: minimumEligibleInvoiceSpend,
    minimumWeightedEligibleAveragePaymentTenor,
    maximumWeightedEligibleAveragePaymentTenor,
    locale: vendorLocale,
    businessEntityScope
  } as types.ExternalVendorFilterParams;

  const {
    data: {
      data: externalVendors = [],
      total: totalVendors,
    } = {},
    isLoading,
    isFetching,
  } = useGetAllExternalVendorsV2Query(filters);

  const onUseLeadListFiltersChanged = (value: boolean) => {
    if (value) {
      setMinimumPercentageProjectedEligibleInvoiceSpendInput('66');
      setMinimumEligibleInvoiceSpendInput('10000');
      setMinimumWeightedEligibleAveragePaymentTenorInput('60');
      setMaximumWeightedEligibleAveragePaymentTenorInput('120');
      setIsInternalVendorInput(BooleanSelectionOptions[2]);
      dispatch(updateIsInternalVendor(false));
      setIsPreferredVendorInput(BooleanSelectionOptions[2]);
      dispatch(updateIsPreferredVendor(false));
      setIsVendorInHubSpotInput(BooleanSelectionOptions[2]);
      dispatch(updateIsVendorInHubSpot(false));
    } else {
      setMinimumPercentageProjectedEligibleInvoiceSpendInput('');
      setMinimumEligibleInvoiceSpendInput('');
      setMinimumWeightedEligibleAveragePaymentTenorInput('');
      setMaximumWeightedEligibleAveragePaymentTenorInput('');
      setIsInternalVendorInput(BooleanSelectionOptions[0]);
      dispatch(updateIsInternalVendor(undefined));
      setIsPreferredVendorInput(BooleanSelectionOptions[0]);
      dispatch(updateIsPreferredVendor(undefined));
      setIsVendorInHubSpotInput(BooleanSelectionOptions[0]);
      dispatch(updateIsVendorInHubSpot(undefined));
    }
    dispatch(updateUseLeadListFilters(value));
    server.setPageNumber(1);
  };

  const onIsInternalVendorChanged = (selection: SingleValue<Selection>) => {
    if (selection?.value === 'true') {
      setIsInternalVendorInput(BooleanSelectionOptions[1]);
      dispatch(updateIsInternalVendor(true));
    } else if (selection?.value === 'false') {
      setIsInternalVendorInput(BooleanSelectionOptions[2]);
      dispatch(updateIsInternalVendor(false));
    } else {
      setIsInternalVendorInput(BooleanSelectionOptions[0]);
      dispatch(updateIsInternalVendor(undefined));
    }
    dispatch(updateUseLeadListFilters(false));
    server.setPageNumber(1);
  };

  const onIsPreferredVendorChanged = (selection: SingleValue<Selection>) => {
    if (selection?.value === 'true') {
      setIsPreferredVendorInput(BooleanSelectionOptions[1]);
      dispatch(updateIsPreferredVendor(true));
    } else if (selection?.value === 'false') {
      setIsPreferredVendorInput(BooleanSelectionOptions[2]);
      dispatch(updateIsPreferredVendor(false));
    } else {
      setIsPreferredVendorInput(BooleanSelectionOptions[0]);
      dispatch(updateIsPreferredVendor(undefined));
    }
    dispatch(updateUseLeadListFilters(false));
    server.setPageNumber(1);
  };

  const onIsVendorInHubSpotChanged = (selection: SingleValue<Selection>) => {
    if (selection?.value === 'true') {
      setIsVendorInHubSpotInput(BooleanSelectionOptions[1]);
      dispatch(updateIsVendorInHubSpot(true));
    } else if (selection?.value === 'false') {
      setIsVendorInHubSpotInput(BooleanSelectionOptions[2]);
      dispatch(updateIsVendorInHubSpot(false));
    } else {
      setIsVendorInHubSpotInput(BooleanSelectionOptions[0]);
      dispatch(updateIsVendorInHubSpot(undefined));
    }
    dispatch(updateUseLeadListFilters(false));
    server.setPageNumber(1);
  };

  const onMinimumPercentageProjectedEligibleInvoiceSpendChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setMinimumPercentageProjectedEligibleInvoiceSpendInput(event.currentTarget.value);
    dispatch(updateUseLeadListFilters(false));
  };

  const onMinimumPercentageProjectedEligibleInvoiceSpendFinalized = (value: string) => {
    const numericValue = parseInt(value, 10);
    dispatch(updateMinimumPercentageProjectedEligibleInvoiceSpend(Number.isNaN(numericValue) ? undefined : numericValue));
    server.setPageNumber(1);
  };

  const onMinimumEligibleInvoiceSpendChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setMinimumEligibleInvoiceSpendInput(event.currentTarget.value);
    dispatch(updateUseLeadListFilters(false));
  };

  const onMinimumEligibleInvoiceSpendFinalized = (value: string) => {
    const numericValue = parseInt(value, 10);
    dispatch(updateMinimumEligibleInvoiceSpend(Number.isNaN(numericValue) ? undefined : numericValue));
    server.setPageNumber(1);
  };

  const onMinimumWeightedEligibleAveragePaymentTenorChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setMinimumWeightedEligibleAveragePaymentTenorInput(event.currentTarget.value);
    dispatch(updateUseLeadListFilters(false));
  };

  const onMinimumWeightedEligibleAveragePaymentTenorFinalized = (value: string) => {
    const numericValue = parseInt(value, 10);
    dispatch(updateMinimumWeightedEligibleAveragePaymentTenor(Number.isNaN(numericValue) ? undefined : numericValue));
    server.setPageNumber(1);
  };

  const onMaximumWeightedEligibleAveragePaymentTenorChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setMaximumWeightedEligibleAveragePaymentTenorInput(event.currentTarget.value);
    dispatch(updateUseLeadListFilters(false));
  };

  const onMaximumWeightedEligibleAveragePaymentTenorFinalized = (value: string) => {
    const numericValue = parseInt(value, 10);
    dispatch(updateMaximumWeightedEligibleAveragePaymentTenor(Number.isNaN(numericValue) ? undefined : numericValue));
    server.setPageNumber(1);
  };

  const appliedFilters = useMemo(() => [isInternalVendor, isPreferredVendor, isVendorInHubSpot, minimumPercentageProjectedEligibleInvoiceSpend, minimumEligibleInvoiceSpend, minimumWeightedEligibleAveragePaymentTenor, maximumWeightedEligibleAveragePaymentTenor]
    .reduce((acc: number, curr) => acc + (curr !== undefined ? 1 : 0), 0), [isInternalVendor, isPreferredVendor, isVendorInHubSpot, minimumPercentageProjectedEligibleInvoiceSpend, minimumEligibleInvoiceSpend, minimumWeightedEligibleAveragePaymentTenor, maximumWeightedEligibleAveragePaymentTenor]);

  const searchParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('search');
  }, [location.search]);

  useEffect(() => {
    if (searchParam) {
      setSearchInput(searchParam);
      dispatch(updateSearchTerm(searchParam));
      onUseLeadListFiltersChanged(false);
    }
  }, [searchParam]);

  return (
    <VendorsPageContainer>
      <FilterSection
        filterControl={(
          <FilterControls>
            <Search
              placeholder="Search by External Vendor Name. Press Enter to search, Escape to clear."
              value={searchInput || ''}
              onChange={(value) => setSearchInput(value)}
              onSubmit={(value) => dispatch(updateSearchTerm(value))}
              onFinalize={(value) => dispatch(updateSearchTerm(value))}
              expanded
              width="550px"
            />
            <DownloadButton
              text="Download CSV"
              data={() => exportAllExternalVendorsV2({
                ...asExportFilter(filters),
              })}
              fileName="external-vendors.csv"
              display="purple"
            />
          </FilterControls>
        )}
        appliedFilters={appliedFilters}
        isInitiallyOpen={!!searchParam}
      >
        <FilterRow>
          <FilterCell>
            <FilterTitle>Partner</FilterTitle>
            <PartnerSelect
              selectedPartner={partner}
              onSelectedPartnerChange={(p) => {
                setPartner(p);
                server.setPageNumber(1);
              }}
              allowAllPartners
            />
          </FilterCell>
          <FilterCell>
            <FilterTitle>Vendor Locale</FilterTitle>
            <VendorLocaleSelect
              selectedVendorLocale={vendorLocale}
              onSelectedVendorLocaleChange={(vl) => {
                dispatch(updateVendorLocale(vl));
                server.setPageNumber(1);
              }}
              allowAllVendorLocales
            />
          </FilterCell>
          <FilterCell>
            <FilterTitle>Use Lead List Filters</FilterTitle>
            <Checkbox
              checked={useLeadListFilters}
              onChange={() => onUseLeadListFiltersChanged(!useLeadListFilters)}
            />
          </FilterCell>
        </FilterRow>
        <FilterRow>
          <FilterCell>
            <FilterTitle>Minimum % Projected Eligible Spend (T6M)</FilterTitle>
            <Input
              type="number"
              placeholder="0 - 100"
              value={minimumPercentageProjectedEligibleInvoiceSpendInput}
              onChange={onMinimumPercentageProjectedEligibleInvoiceSpendChanged}
              onFinalize={(v) => onMinimumPercentageProjectedEligibleInvoiceSpendFinalized(v)}
            />
          </FilterCell>
          <FilterCell>
            <FilterTitle>Minimum Eligible Spend (T6M)</FilterTitle>
            <Input
              type="number"
              placeholder="Value"
              value={minimumEligibleInvoiceSpendInput}
              onChange={onMinimumEligibleInvoiceSpendChanged}
              onFinalize={(v) => onMinimumEligibleInvoiceSpendFinalized(v)}
            />
          </FilterCell>
          <FilterCell>
            <FilterTitle>Minimum Eligible WAPT (T12M)</FilterTitle>
            <Input
              type="number"
              placeholder="Value in days"
              value={minimumWeightedEligibleAveragePaymentTenorInput}
              onChange={onMinimumWeightedEligibleAveragePaymentTenorChanged}
              onFinalize={(v) => onMinimumWeightedEligibleAveragePaymentTenorFinalized(v)}
            />
          </FilterCell>
          <FilterCell>
            <FilterTitle>Maximum Eligible WAPT (T12M)</FilterTitle>
            <Input
              type="number"
              placeholder="Value in days"
              value={maximumWeightedEligibleAveragePaymentTenorInput}
              onChange={onMaximumWeightedEligibleAveragePaymentTenorChanged}
              onFinalize={(v) => onMaximumWeightedEligibleAveragePaymentTenorFinalized(v)}
            />
          </FilterCell>
        </FilterRow>
        <FilterRow>
          <FilterCell>
            <FilterTitle>Is Mesa Vendor</FilterTitle>
            <Select
              value={isInternalVendorInput}
              onChange={onIsInternalVendorChanged}
              options={BooleanSelectionOptions}
            />
          </FilterCell>
          <FilterCell>
            <FilterTitle>Is Preferred Vendor</FilterTitle>
            <Select
              value={isPreferredVendorInput}
              onChange={onIsPreferredVendorChanged}
              options={BooleanSelectionOptions}
            />
          </FilterCell>
          <FilterCell>
            <FilterTitle>Is Vendor In HubSpot</FilterTitle>
            <Select
              value={isVendorInHubSpotInput}
              onChange={onIsVendorInHubSpotChanged}
              options={BooleanSelectionOptions}
            />
          </FilterCell>
        </FilterRow>
      </FilterSection>

      <TableWrapper>
        <TableCardHeader>
          <TableCardTitle>
            <TableTitleIcon />
            {qualifier === 'external' ? 'External Vendors' : 'Internal Vendors'}
            {totalVendors !== undefined && (
              <TableMeta>{totalVendors.toLocaleString()} results</TableMeta>
            )}
          </TableCardTitle>
        </TableCardHeader>
        <MesaTable
          columns={ExternalVendorMesaColumns}
          data={externalVendors}
          dynamicColumnMode
          getRowId={(row) => `${row.partnerId}:${row.externalVendorId}`}
          customStyles={{
            columnFontSize: '10.5px',
            columnFontWeight: 700,
            columnColor: '#6B7280',
          }}
          getRowHref={(row) => `/partners/${row.partnerId}/${qualifier}-vendors/${row.externalVendorId}`}
          isLoading={isLoading || isFetching}
          {...server.bind}
          pagination={{
            ...server.bind.pagination,
            total: totalVendors,
            pageSizeOptions: DefaultPaginationLimits.map((l) => l.value),
          }}
          columnLayout={{ enableResizing: true, enableVisibilityToggle: true, enablePinning: true }}
          minColumnWidth={90}
        />
      </TableWrapper>
    </VendorsPageContainer>
  );
}

export default ExternalVendorsPageTemplate;
